import CardBase from "@/components/cards/CardBase";
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { MultiSelectDropdownWithButton } from "@/components/ui/dropdowns";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  IconProvider,
} from "@/components/ui";
import { ChevronDown } from "lucide-react";
import SubscribeStep1 from "@/components/subscribe_Parts/Step1";
import SubscribeStep2 from "@/components/subscribe_Parts/Step2";
import SubscribeStep3 from "@/components/subscribe_Parts/Step3";
import SubscribeStep4 from "@/components/subscribe_Parts/Step4";
import SubscribeStep5 from "@/components/subscribe_Parts/Step5";
import { submitBetaApplication } from "@/lib/api-functions/api-beta";
import { toast } from "@/hooks/use-toast";
import SubscribeStep6 from "@/components/subscribe_Parts/Step6";

export default function SubscribePage() {
  const [email, setEmail] = useState("");
  const [firstname, setFirstname] = useState("");
  const [lastname, setLastname] = useState("");
  const [jobTitle, setJobTitle] = useState("");
  const [company, setCompany] = useState("");
  const [answer, setAnswer] = useState("");
  const [otherAnswer, setOtherAnswer] = useState("");
  const [useCases, setUseCases] = useState<string[]>([]);
  const [projectStatus, setProjectStatus] = useState<string[]>([]);
  const [gameEngines, setGameEngines] = useState<string[]>([]);
  const [step, setStep] = useState(1);
  const handleSubscribe = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }
    
    try {
      const response = await submitBetaApplication({
        email,
        first_name: firstname,
        last_name: lastname,
        job_title: jobTitle,
        company_name: company,
        use_for: useCases,
        status_of_project: projectStatus,
        engine: gameEngines,
      });

      if (response.success) {
        toast({
          title: "Application Submitted!",
          description: "Thank you for your interest in LocaNest Beta. We'll be in touch soon!",
        });
        // Reset form or redirect
        setStep(1);
        setEmail("");
        setFirstname("");
        setLastname("");
        setJobTitle("");
        setCompany("");
        setAnswer("");
        setOtherAnswer("");
        setUseCases([]);
        setProjectStatus([]);
        setGameEngines([]);
      } else {
        toast({
          title: "Submission Failed",
          description: response.message || "Please try again later.",
          variant: "destructive",
        });
      }
    } catch (error) {
      console.error("Error submitting application:", error);
      toast({
        title: "Submission Failed",
        description: "An unexpected error occurred. Please try again later.",
        variant: "destructive",
      });
    }
  };

  return (
    <div
      className="min-h-screen flex items-center justify-center"
      style={{
        background:
          "linear-gradient(116deg, var(--Purple-1, #6E35FF) 0%, #30E38D 54.16%)",
      }}
    >
      <div className="w-full max-w-2xl flex flex-row p-10 mx-4 gap-4">
        <CardBase className="p-12" shadowType="black">
          {/* Logo */}
          <div className="flex justify-center mb-4">
            <img src="/LocaNest_Purple.svg" alt="LocaNest" className="h-12" />
          </div>
          {/* Welcome Message */}
          <div className="flex flex-row items-center justify-left gap-1 w-full mb-4">
            <button
              onClick={() => {
                if (step === 1) return;
                setStep(step - 1);
              }}
              className={`w-4 h-4 cursor-pointer ${
                step === 1 ? "hidden" : "cursor-pointer text-grey-G4"
              }`}
            >
              <IconProvider iconstring="fa-chevron-left" className="w-4 h-4" />
            </button>
            <h2
              className={`text-xl font-bold text-center text-gray-900 mb-2 ${
                step > 1 ? "hidden" : ""
              }`}
            >
              Request access
            </h2>
          </div>
          <div className="flex flex-row gap-1 w-full mb-4">
            {[1, 2, 3, 4, 5].map((stepNumber) => (
              <div
                key={stepNumber}
                className={`h-2 w-full rounded-full ${
                  step === stepNumber ? "bg-purple-1" : "bg-gray-300"
                }`}
              />
            ))}
          </div>
          {step === 1 && (
            <SubscribeStep1
              onNext={(email) => {
                setEmail(email);
                setStep(2);
              }}
            />
          )}{" "}
          {/* TODO: add email to the state */}
          {step === 2 && (
            <SubscribeStep2
              email={email}
              onNext={(firstname, lastname, jobTitle, company) => {
                setFirstname(firstname);
                setLastname(lastname);
                setJobTitle(jobTitle);
                setCompany(company);
                setStep(3);
              }}
            />
          )}
          {step === 3 && (
            <SubscribeStep3
              email={email}
              onNext={(useCases) => {
                setUseCases(useCases);
                setStep(4);
              }}
            />
          )}
          {step === 4 && (
            <SubscribeStep4
              onNext={(projectStatus) => {
                setProjectStatus(projectStatus);
                setStep(5);
              }}
            />
          )}
          {step === 5 && (
            <SubscribeStep5
              onNext={async (gameEngines) => {
                setGameEngines(gameEngines);
                
                // Submit with the engines data directly since state update is async
                try {
                  const response = await submitBetaApplication({
                    email,
                    first_name: firstname,
                    last_name: lastname,
                    job_title: jobTitle,
                    company_name: company,
                    use_for: useCases,
                    status_of_project: projectStatus,
                    engine: gameEngines, // Use the parameter directly
                  });

                  if (response.success) {
                    toast({
                      title: "Application Submitted!",
                      description: "Thank you for your interest in LocaNest Beta. We'll be in touch soon!",
                    });
                  } else {
                    toast({
                      title: "Submission Failed",
                      description: response.message || "Please try again later.",
                      variant: "destructive",
                    });
                  }
                } catch (error) {
                  console.error("Error submitting application:", error);
                  toast({
                    title: "Submission Failed",
                    description: "An unexpected error occurred. Please try again later.",
                    variant: "destructive",
                  });
                }
                
                setStep(6);
              }}
            />
          )}
          {step === 6 && (
            <SubscribeStep6      
              onNext={() => {
                setStep(1);
                setEmail("");
                setFirstname("");
                setLastname("");
                setJobTitle("");
                setCompany("");
                setAnswer("");
                setOtherAnswer("");
                setUseCases([]);
                setProjectStatus([]);
                setGameEngines([]);
              }}
              onSubmit={() => console.log("Submitted")}
            />
          )}
        </CardBase>
      </div>
    </div>
  );
}
